import {Component, OnDestroy, OnInit} from '@angular/core';
import {BehaviorSubject, combineLatest, Observable, of, Subject} from 'rxjs';
import {OrderHistory, ReduxPagination} from '@kion/data-models';
import {UsersFacade} from '@kion/data-access/data-access-users';
// tslint:disable-next-line:nx-enforce-module-boundaries
import { DropDownOption } from '../../../../../../shared/ui/src/lib/shared/elements/drop-down/drop-down.component';
// tslint:disable-next-line:nx-enforce-module-boundaries
import { FilterChip } from '../../../../../../shared/ui/src/lib/shared/elements/filter-chips/filter-chips.component';
import {TranslateService} from '@ngx-translate/core';
import {
  distinctUntilChanged,
  filter,
  map,
  switchMap, take
} from "rxjs/operators";
import {SubSink} from "subsink";
import {CorrelationObserverService} from "@kion/ngrx-helper";
import {ActivatedRoute} from "@angular/router";
import {OrderHistoryFacade} from "@kion/data-access/data-access-orderhistory";

@Component({
  selector: 'kion-cms-order-history',
  templateUrl: './order-history.component.html',
  styleUrls: ['./order-history.component.scss']
})
export class OrderHistoryComponent implements OnInit, OnDestroy {
  orders$: Observable<OrderHistory[]>;
  userId: string;
  pagination$: Observable<ReduxPagination>;
  query$: Observable<string>;
  resetValue = false;
  loading = false;
  statusFilterSelected = null;
  truckId: string;
  sorts$: BehaviorSubject<DropDownOption[] | null>  = new BehaviorSubject(null);
  sortDefaults = [{
    key: 'purchaseDate-asc',
    value: 'purchaseDate-asc',
    selected: true,
  } as DropDownOption,
    {
      key: 'purchaseDate-desc',
      value: 'purchaseDate-desc',
      selected: false,
    } as DropDownOption,
    {
      key: 'orderNumber-asc',
      value: 'orderNumber-asc',
      selected: false,
    } as DropDownOption,
    {
      key: 'orderNumber-desc',
      value: 'orderNumber-desc',
      selected: false,
    } as DropDownOption];

  private readonly subSink = new SubSink();

  statusFilterChipDefaults = [{
    id: 'ordered',
    name: 'order.history.status.ordered',
    active: false,
    quantity: '',
  } as FilterChip,
    {
      id: 'invoiced',
      name: 'order.history.status.invoiced',
      active: false,
      quantity: '',
    } as FilterChip,
    {
      id: 'installed',
      name: 'order.history.status.installed',
      active: false,
      quantity: '',
    } as FilterChip,
    {
      id: 'cancelled',
      name: 'order.history.status.cancelled',
      active: false,
      quantity: '',
    } as FilterChip,
  ];

  private statusFilterChips$ = new BehaviorSubject<FilterChip[]>([]);
  private currentBuyerChip = {
    id: 'buyer',
    name: 'order.history.status.myOrders',
    quantity: '',
    active: false,
  } as FilterChip;
  private filterChipCurrentBuyer$ = new BehaviorSubject<FilterChip[]>([]);
  filterChips$: Observable<FilterChip[]> = combineLatest([this.statusFilterChips$,this.filterChipCurrentBuyer$]).pipe(
      map(([statusChips,buyerChip]) => statusChips?.concat(buyerChip ?? [])),
      distinctUntilChanged(),
  );
  triggerChipSelect$ = new Subject<FilterChip>();

  private searchTrigger$ = new Subject<void>();

  searchTerm$ = new BehaviorSubject<string>('');
  private searchData$ = combineLatest([this.searchTerm$, this.filterChips$, this.sorts$, this.usersFacade.all$]).pipe();

  constructor(
     private orderHistoryFacade: OrderHistoryFacade,
     private observer: CorrelationObserverService,
     private usersFacade: UsersFacade,
     private translationService: TranslateService,
     private activatedRoute: ActivatedRoute
  ) {
    this.orders$ = this.orderHistoryFacade.searchResults$;
  }


  ngOnInit(): void {

    this.searchData$.pipe(
        switchMap(([searchTerm, filterChips,sorts,users]) =>
            this.searchTrigger$.pipe(
                map(() =>({searchTerm, filterChips,sorts,users}))
            )
        )
    ).subscribe(({searchTerm, filterChips, sorts, users}) => {
      const statusValues = (filterChips.map((item) => item.active && item.id !== 'buyer' ? item.id : ''))
          .filter((item) => item !== '') .join(',');
      const statusQuery = statusValues.length ? 'status:' + statusValues : '';

      const myOrders = !!(users.length && filterChips.find((item) => item.active && item.id === 'buyer'));
      const query = (searchTerm.length !== 0 && statusQuery.length === 0 ) ? searchTerm :
          (searchTerm.length === 0 && statusQuery.length !== 0) ? statusQuery :
              (searchTerm.length !== 0 && statusQuery.length !== 0) ? `${searchTerm}:${statusQuery}` :
                  '';
      const activeSort = sorts?.find((sort) => sort.selected)
          ?? this.sortDefaults.find((sort) => sort.selected);

      this.orderHistoryFacade.search(query ?? null, false, false, activeSort.key,
          false, undefined, myOrders, this.truckId);
      this.orderHistoryFacade.updateQuery(query);
    });

    this.loading = true;
    this.query$ = this.orderHistoryFacade.query$;
    this.pagination$ = this.orderHistoryFacade.pagination$;

    this.orders$.pipe(
    ).subscribe(() => this.loading = false);

    setTimeout(() => {
      this.sortDefaults.forEach((option) => {
        option.value = this.translationService.instant('order.history.sortBy.' + option.value);
      });
      this.sorts$.next(this.sortDefaults);
      this.searchTrigger$.next();
    }, 1000);



    this.statusFilterChips$.next(this.statusFilterChipDefaults);
    this.filterChipCurrentBuyer$.next([this.currentBuyerChip]);

    this.triggerChipSelect$.pipe(
        filter((triggerChipSelect) => !!triggerChipSelect),
        distinctUntilChanged(),
    ).subscribe((triggeredChip)=> {
      const statusChips = this.statusFilterChips$.getValue();
      const currentBuyerChip = this.filterChipCurrentBuyer$.getValue();

      if(triggeredChip.id === currentBuyerChip[0].id){
        this.filterChipCurrentBuyer$.next([{...triggeredChip, active: !triggeredChip.active}]);
      }
      else{
        this.statusFilterChips$.next(statusChips.map((currentChip) => triggeredChip.id === currentChip.id ? {
          ...currentChip,
          active: !triggeredChip.active
        } : currentChip));
      }

      this.pageChanged(0);
    });

    this.activatedRoute.queryParams.subscribe(params => {
      if (params['truckId'] !== null && params['truckId'] !== undefined) {
        this.searchTerm$.next(params['truckId']);
        this.searchTrigger$.next();
      }
    });
  }

  perPageChanged(perPage: number): void {
    this.loading = true;
    this.subSink.sink = this.observer.subscribeToAction(
       this.orderHistoryFacade.updatePerPage(perPage),
       () =>
          this.query$
             .pipe(take(1))
             .subscribe((query) => this.searchTrigger$.next())
    );
  }

  pageChanged(page: number): void {
    this.loading = true;
    this.subSink.sink = this.observer.subscribeToAction(this.orderHistoryFacade.updatePage(page), () =>
       this.query$
          .pipe(take(1))
          .subscribe((query) => this.searchTrigger$.next())
    );
  }

  ngOnDestroy() {
    this.subSink.unsubscribe();
  }

  search(searchTerm: string) {
    this.searchTerm$.next(searchTerm);
    this.pageChanged(0);
  }

  searchBoxResetValue() {
    this.resetValue = false;
  }

  searchBoxCleared() {
    this.loading = true;
    this.searchTerm$.next('');
    this.pageChanged(0);
  }

  sortChanged(value: DropDownOption) {
    this.sorts$.next( this.sortDefaults
       .map((option) =>
          option.key === value.key ? {...option, selected: true} : {...option, selected: false}));
    this.statusFilterSelected = value.key;
    this.searchTrigger$.next();
  }

  updateFilter(chip: FilterChip){
    this.triggerChipSelect$.next(chip);
  }

  get linkDetails(): string {
    return '/order-history/';
  }
}
